<template>
    <div class="gallery-container"
         ref="gallery"
         @scroll="throttledScroll">
        <div id="grid" v-if="loaded">
            <div class="grid-item">
                <div class="add-button"
                     @click="showUploadDialog">
                    +
                </div>
            </div>
            <div class="grid-item-container"
                 v-for="(item, index) in loadedList"
                 :key="index">
                <div v-if="breakingPoint(index)"
                     class="date-header">
                    {{new Date(item.lastModified).toDateString()}}
                </div>
                <div class="grid-item">
                    <progressive-background class="grid-img"
                                            :placeholder="`${item.url}?x-oss-process=style/gallery-thumbnail`"
                                            :src="`${item.url}?x-oss-process=style/gallery`"
                                            :aspect-ratio="1">
                        <div class="img-controls"
                             slot="content">
                            <div @click="enlarge(item.url)">查看大图</div>
                            <div @click="copy(item.url)">复制链接</div>
                        </div>
                    </progressive-background>
                </div>
            </div>
        </div>
        <div id="loading-indicator" v-else>
            加载中...
        </div>
        <div id="modal-mask"
             v-if="modalState !== 0"
             @click="dismissModal"></div>
        <div id="upload-dialog"
             class="modal-dialog"
             v-if="modalState === 1">
            <h2>上传图片</h2>
            <label for="input-file"
                   id="upload-label"
                   :class="{'disabled': uploadDisabled}"
                   @dragover.prevent="highlight"
                   @dragleave.prevent="unhighlight"
                   @drop.stop.prevent="drop"
                   v-html="uploadMessage">
            </label>
            <form id="file-form">
                <input type="file"
                       ref="input"
                       id="input-file"
                       @change="uploadFile"
                       accept=".jpg,.jpeg,.png,image/*"
                       :disabled="uploadDisabled"/>
            </form>
            <div class="button modal-button"
                 @click="dismissModal">完成
            </div>
        </div>
        <div id="image-dialog"
             class="modal-dialog"
             v-if="modalState === 2"
             @click="dismissModal">
            <img class="large-img"
                 :alt="viewedImage"
                 :src="`${viewedImage}?x-oss-process=style/large`"/>
        </div>
    </div>
</template>

<script>
	import GalleryService from "../services/GalleryService";

	export default {
		name: "Gallery",
		data() {
			return {
				list: [],
				loadedList: [],
				loaded: false,
				loadLimit: 20,
				modalState: 0,
				uploadMessage: "点击或拖拽上传。",
				uploadDisabled: false,
				throttledScroll: this.$throttle(this.onScroll, 100),
				viewedImage: ""
			}
		},
		async created() {
			await GalleryService.init();
			await this.reload();
			this.loaded = true;
		},
		methods: {
			async reload() {
				let list = await GalleryService.list();
				this.list = list.reverse();
				this.loadedList = [];
				this.loadMore();
			},
			loadMore() {
				let offset = this.loadedList.length;
				if (offset !== this.list.length) {
					this.loadedList = this.loadedList.concat(this.list.slice(offset, offset + this.loadLimit));
				}
			},
			copy(link) {
				navigator.clipboard.writeText(link).then(() => {
					alert("复制成功");
				}).catch(() => {
					alert("复制失败，请手动复制\n" + link);
				});
			},
			showUploadDialog() {
				this.uploadMessage = "点击或拖拽上传。";
				this.modalState = 1;
			},
			dismissModal() {
				this.modalState = 0;
			},
			breakingPoint(x) {
				if (x === 0) {
					return true;
				}
				let prev = new Date(this.loadedList[x - 1].lastModified).toLocaleDateString();
				let curr = new Date(this.loadedList[x].lastModified).toLocaleDateString();
				return prev !== curr;
			},
			async uploadFile() {
				let path = this.$refs.input.files[0];
				this.uploadDisabled = true;
				this.uploadMessage = "上传中...";
				try {
					await GalleryService.upload(path);
					this.uploadMessage = `上传成功。点击或拖拽以继续上传。`
					this.reload().then();
				} catch (e) {
					this.uploadMessage = `上传失败。错误信息：<br />${e}<br />\n点击或拖拽以重新上传。`
				} finally {
					this.uploadDisabled = false;
				}
			},
			onScroll() {
				let container = this.$refs.gallery;
				if (container.scrollTop >= 0.75 * (container.scrollHeight - container.clientHeight)) {
					this.loadMore();
				}
			},
			enlarge(url) {
				this.viewedImage = url;
				this.modalState = 2;
			},
			drop(e) {
				this.unhighlight();
				try {
					let data = e.dataTransfer;
					console.log(data.files);
					let files = [...data.files];
					if (files.length > 1) {
						this.uploadMessage = "仅支持单个文件，请重试"
						return;
					}
					this.$refs.input.files = data.files;
					this.uploadFile();
				} catch (ex) {
					alert("拖拽失败，详细请参考调试输出")
					console.log(e.dataTransfer, ex);
				}
			},
			highlight() {
				this.uploadMessage = "拖拽至此";
			},
			unhighlight() {
				this.uploadMessage = "点击或拖拽上传。";
			}
		}
	}
</script>

<style scoped lang="scss">
    .gallery-container {
        padding: 64px;
        height: 100vh;
        box-sizing: border-box;
        overflow: scroll;
    }

    #loading-indicator {
        text-align: center;
        line-height: calc(100vh - 128px);
    }

    #grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        grid-gap: 16px;
    }

    .grid-item-container {
        display: contents;
    }

    .grid-item {
        text-align: center;
    }

    .grid-item::after {
        content: "";
        width: 100%;
        height: 0;
        padding-bottom: 100%;
    }

    .grid-img, .add-button {
        position: relative;
        width: 100%;
        user-select: none;
        background-color: transparentize($d-fg, 0.4);
    }

    .add-button {
        height: 0;
        padding-top: calc(50% - 80px);
        padding-bottom: calc(50% + 80px);
        background-color: $d-fg;
        font-size: 116px;
        box-sizing: border-box;
        color: $p-bg;
    }

    .add-button::after {
        position: absolute;
        z-index: 1;
        content: "";
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        cursor: pointer;
        background-color: transparent;
        transition: background-color 0.3s $bezier;
        color: $p-bg;
    }

    .add-button:hover::after {
        background-color: transparentize($p-fg, 0.5);
    }

    .add-button:active {
        color: transparentize($p-bg, 0.5);
    }

    .img-controls {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 0;
        padding-top: 50%;
        color: $p-bg;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .img-controls:hover > div {
        opacity: 1;
    }

    .img-controls > div {
        background-color: transparentize($p-fg, 0.5);
        transition: background-color 0.3s $bezier, opacity 0.3s $bezier;
        opacity: 0;

        &:hover {
            background-color: transparentize($p-fg, 0.2);
        }

        &:active {
            color: transparentize($p-bg, 0.5);
        }

        &:first-child {
            padding-top: calc(40% - 16px);
            padding-bottom: 10%;
            height: 16px;

            &::after {
                position: absolute;
                content: "";
                left: 10%;
                right: 10%;
                bottom: 0;
                border-bottom: solid 1px $d-fg;
            }
        }

        &:last-child {
            padding-bottom: calc(40% - 16px);
            padding-top: 10%;
            height: 16px;
        }
    }

    .date-header {
        position: relative;
        grid-column: 1 / -1;
        font-family: $serif;
        border-left: solid 0.3em $p-accent;
        padding-left: 1em;
        margin-top: 32px;
    }

    #modal-mask {
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        background-color: transparentize($p-fg, 0.5);
    }

    .modal-dialog {
        position: fixed;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        z-index: 3;
        animation: modal-show 0.5s $bezier;
    }

    %dialog-box {
        background-color: $p-bg;
        border: $solid;
    }

    #upload-dialog {
        left: 25vw;
        top: 25vh;
        height: 50vh;
        width: 50vw;
        padding: 24px 32px;
        @extend %dialog-box;

        h2 {
            font-family: $serif;
        }
    }

    #image-dialog {
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        max-width: 80vw;
        max-height: 80vh;
    }

    .large-img {
        position: relative;
        max-height: 100%;
        max-width: 100%;
        margin: auto;
        display: block;
        @extend %dialog-box;
        box-shadow: $p-accent 0 16px 0 -8px;
    }

    #upload-label {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex-grow: 1;
        border: $dotted;
        transition: background-color 0.3s $bezier;
        margin-bottom: 24px;
        text-align: center;
        padding: 0 24px;
        box-sizing: border-box;
        user-select: none;

        div {
            text-align: center;
        }
    }

    #upload-label.disabled {
        background-color: transparentize($p-fg, 0.95);
        border: solid 1px $d-fg;
    }

    #upload-label:hover {
        background-color: transparentize($p-fg, 0.95);
    }

    .modal-button {
        align-self: flex-end;
        width: 64px;
    }

    @keyframes modal-show {
        from {
            opacity: 0;
            transform: translateY(-2em);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    #input-file {
        display: none;
    }
</style>